/**
 * Artifact Data Hooks (Notes, Attachments, etc.)
 *
 * TanStack Query hooks for artifact CRUD operations.
 * Artifacts are content items that belong to projects or workspaces.
 */

import { createElement } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ApiResponse,
  ArtifactListSortBy,
  LinkedProtocolRef,
  PaginatedResponse,
  ParentEntityType,
} from '@bower/shared';
import { apiService } from '../../services/api.service';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { restoreTrashItem } from '../../services/api/workspace-admin.api';
import {
  createArtifact,
  deleteArtifact,
  getAllArtifacts,
  getArtifact,
  getProjectArtifacts,
  updateArtifact,
} from '../../services/api/artifacts.api';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { useAppToast } from '../ui/useAppToast';
import { ActionToast } from '../../components/ui/ActionToast';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useInfinitePaginated } from './useInfinitePaginated';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';

/**
 * Full artifact shape as returned by the API.
 * Mirrors ArtifactDoc from @bower/shared but uses frontend-friendly types.
 */
export interface Artifact {
  id: string;
  workspaceId: string;
  artifactType: string;
  parentEntityType: ParentEntityType;
  parentEntityId: string;
  title: string | null;
  content?: Record<string, any> | string;
  captureMethod?: string;
  captureSurface?: string;
  captureDate?: string;
  metadata: Record<string, any>;
  tags: string[];
  version?: number;
  processingStatus?: string;
  processingData?: Record<string, any>;
  /** A spreadsheet's bound workbook; null while its import is running or failed. */
  univerUnitId?: string | null;
  isTemplate?: boolean;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  markdown?: string;
  // Legacy/computed fields returned by some API endpoints
  projectId?: string;
  userId?: string;
  /**
   * True when this artifact has its own EntityPrivacy row with
   * mode=RESTRICTED.  The list query surfaces this so the row can
   * render a lock badge without per-entity ACL fetches.  False (or
   * absent) on workspaces that haven't enabled ACL enforcement.
   */
  isRestricted?: boolean;
  /**
   * For derived notes (captureMethod = audio_to_text / image_to_text / …),
   * the original source attachment that the note was extracted from.
   * Set by the paginated list endpoint via a Relationship join; absent
   * when no provenance edge exists or when the row isn't a derived note.
   * Used by Capture Stream to render an inline audio player or thumbnail
   * next to the transcribed note.
   */
  linkedSource?: {
    id: string;
    artifactType: string;
    title: string | null;
    url: string | null;
    thumbnailUrl: string | null;
    mimeType: string | null;
    fileName: string | null;
    durationMs: number | null;
  };
  /**
   * The protocol this artifact was captured during, resolved through
   * the `captured_during` relationship edge. Populated by the
   * paginated list endpoint for captures made while a Protocol
   * Execution run was active. Drives the Capture Stream's "during *X*"
   * pill and is the key for session-as-container grouping.
   *
   * The SHAPE is `@bower/shared`'s, not ours. The backend builds this
   * projection and the Capture Stream renders every field of it, so
   * neither package owns it — and hand-duplicating a 14-field interface
   * on both sides meant the next field added to one was silently absent
   * from the other.
   */
  linkedProtocol?: LinkedProtocolRef;
  /**
   * The parent collection's name, when the parent is a collection the caller
   * can read and it is not in the trash. Set by the paginated list endpoint,
   * so a list page never has to fetch every collection to name its rows.
   * Absent otherwise: the row then reads "Restricted collection".
   */
  parentCollectionName?: string;
  /** A protocol's version count, drafts included (the Protocols list's Versions column). */
  versionCount?: number;
}

interface CreateNoteData {
  // Support both 'name' and 'title' for flexibility
  name?: string;
  title?: string;
  content?: string;
  projectId?: string;
  // Support the associatedParent format the create-note callers pass
  associatedParent?: {
    parentEntityType: 'project' | 'workspace';
    parentEntityId: string;
  };
}

interface UpdateArtifactData {
  id: string;
  name?: string;
  title?: string;
  content?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  // Support parent relocation
  parentEntityType?: ParentEntityType;
  parentEntityId?: string;
}

export interface ArtifactListFilters {
  readonly parentEntityType?: 'project' | 'workspace' | 'artifact';
  readonly parentEntityId?: string;
  readonly artifactType?: string;
  /**
   * Multi-select Type chip on the Artifacts list page. Sent as a
   * comma-separated `artifactType` query param (the backend parses it as a
   * list). Takes precedence over the single `artifactType` when both are set.
   */
  readonly artifactTypes?: ReadonlyArray<string>;
  readonly searchTerm?: string;
  /**
   * Server sort field, from the shared `ARTIFACT_SORT_BY` SSOT. The backend's
   * `services/artifact/artifact.sort-fields.ts` maps each value to a column
   * and is the authority on which columns are safe to sort on — the list is
   * keyset-paginated, so a nullable column or a relation silently drops rows
   * between pages. Widen the shared list and that map together. The Protocols
   * list's computed sorts (`PROTOCOL_COMPUTED_SORTS`: `lastRun`, `versions`)
   * are accepted only on a protocols-only listing (VALIDATION_ERROR otherwise).
   */
  readonly sortBy?: ArtifactListSortBy;
  readonly sortOrder?: 'asc' | 'desc';
  readonly limit?: number;
  /** Filter by creator. Backend reads as a comma-separated `createdBy` param. */
  readonly createdByIds?: ReadonlyArray<string>;
  /** Filter by `Artifact.captureMethod` (e.g. `audio_to_text`,
   * `live_recording`). Backend reads as comma-separated `captureMethod`. */
  readonly captureMethods?: ReadonlyArray<string>;
  /** Filter by `Artifact.captureSurface` (mobile / desktop / live_session /
   * agent_chat / connector / api). Drives Capture Stream's Device chip;
   * backend reads as comma-separated `device`. */
  readonly captureSurfaces?: ReadonlyArray<string>;
  /** Inclusive ISO timestamps on `createdAt`. */
  readonly createdFrom?: string;
  readonly createdTo?: string;
  /** Multi-collection filter (project IDs). Backend reads as
   * comma-separated `collection` query param. */
  readonly projectIds?: ReadonlyArray<string>;
  /** Restrict to artifacts with a `captured_during` edge to one of these
   * protocols. Drives Capture Stream's Protocol chip; backend reads as
   * comma-separated `protocol`. */
  readonly capturedDuringProtocolIds?: ReadonlyArray<string>;
  /** Only protocols with an active or paused run (the Protocols list's
   * "Active runs" toggle). Backend reads `hasOpenRuns=true`. */
  readonly hasOpenRuns?: boolean;
  /** Only these artifacts (at most 100; the Protocols list's Protocol filter).
   * Backend reads as comma-separated `ids`. */
  readonly ids?: ReadonlyArray<string>;
  /** Permanently exclude artifact types from the result. Backend reads as
   * comma-separated `excludeArtifactType` param. Used by pages that
   * categorically hide certain types (e.g. /artifacts excludes equipment). */
  readonly excludeArtifactTypes?: ReadonlyArray<string>;
  /** Rows carrying ANY of these tags. Backend reads as comma-separated `tags`. */
  readonly tags?: ReadonlyArray<string>;
  /** See useInfinitePaginated.eagerlyFetchAll — bulk-ops pages that still
   * do client-side filtering pass `true` to auto-load all pages. */
  readonly eagerlyFetchAll?: boolean;
}

/**
 * Infinite-scroll version of artifact listing, for any list that wants a
 * cursor.
 * Changes to `filters` rotate the queryKey so first-page `totalCount` is
 * always fresh after filter toggles.
 */
export function useInfiniteArtifacts(
  filters: ArtifactListFilters = {},
  /** `enabled: false` holds the query (TanStack `enabled`). */
  options: { readonly enabled?: boolean } = {},
) {
  const workspaceId = useWorkspaceId();
  const parentId = filters.parentEntityId;
  const parentType = filters.parentEntityType;

  return useInfinitePaginated<Artifact>({
    queryKey: ['artifacts', 'infinite', { workspaceId }, filters] as const,
    fetchPage: async ({ cursor, limit }) => {
      const params: Record<string, string | number> = { limit };
      if (cursor) params.cursor = cursor;
      if (filters.searchTerm) params.search = filters.searchTerm;
      if (filters.artifactTypes && filters.artifactTypes.length > 0) {
        // Multi-select Type chip → comma-separated; backend parses as a list.
        params.artifactType = filters.artifactTypes.join(',');
      } else if (filters.artifactType) {
        params.artifactType = filters.artifactType;
      }
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.createdByIds && filters.createdByIds.length > 0) {
        params.createdBy = filters.createdByIds.join(',');
      }
      if (filters.captureMethods && filters.captureMethods.length > 0) {
        params.captureMethod = filters.captureMethods.join(',');
      }
      if (filters.captureSurfaces && filters.captureSurfaces.length > 0) {
        params.device = filters.captureSurfaces.join(',');
      }
      if (filters.createdFrom) params.from = filters.createdFrom;
      if (filters.createdTo) params.to = filters.createdTo;
      if (filters.projectIds && filters.projectIds.length > 0) {
        params.collection = filters.projectIds.join(',');
      }
      if (filters.capturedDuringProtocolIds && filters.capturedDuringProtocolIds.length > 0) {
        params.protocol = filters.capturedDuringProtocolIds.join(',');
      }
      if (filters.excludeArtifactTypes && filters.excludeArtifactTypes.length > 0) {
        params.excludeArtifactType = filters.excludeArtifactTypes.join(',');
      }
      if (filters.tags && filters.tags.length > 0) params.tags = filters.tags.join(',');
      if (filters.hasOpenRuns) params.hasOpenRuns = 'true';
      if (filters.ids && filters.ids.length > 0) params.ids = filters.ids.join(',');

      // Route to the right REST endpoint based on parent scope. All three
      // return the same PaginatedResponse<Artifact> shape — the split is
      // purely to keep URLs meaningful.
      let response;
      if (parentType === 'project' && parentId) {
        response = await getProjectArtifacts(apiService, parentId, params);
      } else if (parentType === 'workspace' || !parentType) {
        response = await getAllArtifacts(apiService, {
          ...params,
          ...(parentType ? { parentEntityType: parentType } : {}),
        });
      } else {
        // artifact-as-parent (attachments-under-note) — use generic endpoint
        response = await getAllArtifacts(apiService, {
          ...params,
          parentEntityType: parentType,
          ...(parentId ? { parentEntityId: parentId } : {}),
        });
      }

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch artifacts');
      }
      return response.data as PaginatedResponse<Artifact>;
    },
    limit: filters.limit,
    enabled: !!workspaceId && options.enabled !== false,
    eagerlyFetchAll: filters.eagerlyFetchAll,
  });
}

/**
 * Fetch a single artifact by ID
 */
export function useArtifact(artifactId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.artifacts.detail(artifactId || ''),
    queryFn: async () => {
      if (!artifactId) throw new Error('Artifact ID is required');
      const response = await getArtifact(apiService, artifactId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch artifact');
      }
      return response.data as Artifact;
    },
    enabled: !!artifactId,
    // Seed from any cached list that already has this row so list → detail
    // feels instant. TanStack still refetches to fill in fields the list
    // didn't include (e.g. full TipTap content).
    placeholderData: () => findCachedArtifact(queryClient, artifactId),
  });
}

/**
 * Imperative, cache-deduped artifact fetch for non-hook call sites (the
 * breadcrumb's parent-artifact lookup). Shares `queryKeys.artifacts.detail`
 * with `useArtifact` so an already-loaded parent is served from cache.
 * Throws on failure, like `useArtifact`'s queryFn.
 */
export function fetchArtifactCached(artifactId: string): Promise<Artifact> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.artifacts.detail(artifactId),
    queryFn: async () => {
      const response = await getArtifact(apiService, artifactId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch artifact');
      }
      return response.data as Artifact;
    },
  });
}

// Exported for unit testing. Accepts the QueryClient as a param so tests can
// pass a fresh client without touching the app-level singleton.
export function findCachedArtifact(
  qc: QueryClient,
  artifactId: string | undefined,
): Artifact | undefined {
  if (!artifactId) return undefined;
  const lists = qc.getQueriesData<Artifact[]>({ queryKey: ['artifacts'] });
  for (const [, list] of lists) {
    if (Array.isArray(list)) {
      const match = list.find((a) => a?.id === artifactId);
      if (match) return match;
    }
  }
  return undefined;
}

/**
 * Create a note (convenience wrapper for artifact creation)
 */
export function useCreateNote() {
  const queryClient = useQueryClient();

  return useMutation({
    // Not retried, for the reason on useCreateArtifact below.
    retry: false,
    mutationFn: async (data: CreateNoteData) => {
      // Determine parent info from associatedParent or direct IDs
      let parentEntityType: string | undefined;
      let parentEntityId: string | undefined;

      if (data.associatedParent) {
        parentEntityType = data.associatedParent.parentEntityType;
        parentEntityId = data.associatedParent.parentEntityId;
      } else if (data.projectId) {
        parentEntityType = 'project';
        parentEntityId = data.projectId;
      }

      if (!parentEntityId) {
        throw new Error('Note must belong to a project or workspace');
      }

      // Use title or name (title takes precedence for backwards compatibility)
      const noteTitle = data.title || data.name;

      const response = await apiService.post<Artifact>('/artifacts', {
        title: noteTitle,
        artifactType: 'note',
        associatedParent: {
          parentEntityType,
          parentEntityId,
        },
        content: data.content || undefined,
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to create note');
      }
      return response.data as Artifact;
    },
    onSuccess: (newArtifact) => {
      // Resolve the effective project ID from either legacy fields or parent relationship
      const effectiveProjectId =
        newArtifact.projectId ||
        (newArtifact.parentEntityType === 'project' ? newArtifact.parentEntityId : undefined);

      // Invalidate parent's artifact list AND project children
      if (effectiveProjectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.artifacts.all('project', effectiveProjectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.children(effectiveProjectId),
        });
      }
      // Force hierarchy and recents to refetch immediately
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['recents'] });
    },
  });
}

/**
 * Create a generic artifact
 */
export function useCreateArtifact() {
  const queryClient = useQueryClient();

  return useMutation({
    // Not the app's default single retry: a create is not idempotent, and a
    // refusal (a 4xx such as a trashed parent) would be sent twice and reach
    // the user a couple of seconds late. The HTTP layer still retries a
    // network or 5xx failure once here (unlike collections and equipment),
    // so a lost response can still make a second item.
    retry: false,
    // ApiResponseError keeps the code, so a caller can tell a refused parent
    // (INVALID_PARENT) from any other failure. The message is unchanged.
    mutationFn: async (data: Record<string, unknown>) =>
      unwrapApiResponse(
        (await createArtifact(apiService, data)) as ApiResponse<Artifact>,
        'Failed to create artifact',
      ),
    onSuccess: (newArtifact) => {
      const effectiveProjectId =
        newArtifact.projectId ||
        (newArtifact.parentEntityType === 'project' ? newArtifact.parentEntityId : undefined);

      if (effectiveProjectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.artifacts.all('project', effectiveProjectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.children(effectiveProjectId),
        });
      }
      // Force hierarchy and recents to refetch immediately
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['recents'] });
    },
  });
}

/**
 * Update an artifact with optimistic UI update.
 *
 * `scope` (TanStack v5 mutation scopes) runs this instance's mutations one at
 * a time, in call order, for writes that must not race (the note-width
 * toggle). Omitted, mutations run concurrently as before.
 *
 * `mutationKey` lets queued writes defer to the latest one: while another
 * mutation with the same key is pending for the same row, a settling one skips
 * both its rollback (on failure) and its detail refetch, so neither can
 * overwrite the pending write's optimistic value. The last one to settle for
 * that row rolls back if it failed and refetches.
 */
export function useUpdateArtifact(options?: {
  scope?: { id: string };
  mutationKey?: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const mutationKey = options?.mutationKey;
  // The settling mutation still counts as pending inside onError/onSettled
  // (TanStack v5 marks it settled after both run), so > 1 means another write
  // with this key to the same row is still queued or in flight. Counted per
  // row, so a key shared across artifacts never defers another row's
  // rollback. Unkeyed callers never defer.
  const othersPending = (id: string) =>
    !!mutationKey &&
    queryClient.isMutating({
      mutationKey,
      predicate: (m) => (m.state.variables as UpdateArtifactData | undefined)?.id === id,
    }) > 1;

  return useMutation({
    scope: options?.scope,
    mutationKey,
    mutationFn: async (data: UpdateArtifactData) => {
      const response = await updateArtifact(apiService, data.id, data);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to update artifact');
      }
      return response.data as Artifact;
    },
    // Optimistic update
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.artifacts.detail(data.id) });

      const previousArtifact = queryClient.getQueryData<Artifact>(
        queryKeys.artifacts.detail(data.id),
      );

      // Optimistically update detail
      if (previousArtifact) {
        queryClient.setQueryData<Artifact>(queryKeys.artifacts.detail(data.id), {
          ...previousArtifact,
          ...data,
          // The server merges a metadata patch (applyMetadataPatch in
          // services/artifact/artifact.metadata-patch.ts); mirror it.
          ...(data.metadata && {
            metadata: { ...previousArtifact.metadata, ...data.metadata },
          }),
        });
      }

      return { previousArtifact };
    },
    // Roll back only the fields this mutation sent, onto the CURRENT row. The
    // detail key can be re-seeded while the request is in flight (the note
    // page's autosave does), and restoring the whole onMutate snapshot would
    // put an older body and `updatedAt` back. Metadata is restored per sent
    // sub-key, mirroring the server's metadata merge. With a later same-key
    // write to this row still pending, skip the rollback: this snapshot would
    // overwrite that write's optimistic value, and the last write to settle
    // refetches.
    onError: (_err, data, context) => {
      const previous = context?.previousArtifact;
      const key = queryKeys.artifacts.detail(data.id);
      if (previous && !othersPending(data.id)) {
        queryClient.setQueryData<Artifact>(key, (current) => {
          if (!current) return previous;
          const restored: Record<string, unknown> = { ...current };
          const prev = previous as unknown as Record<string, unknown>;
          for (const field of Object.keys(data)) {
            if (field === 'id' || field === 'metadata') continue;
            if (field in prev) restored[field] = prev[field];
            else delete restored[field];
          }
          if (data.metadata) {
            const metadata: Record<string, unknown> = { ...current.metadata };
            for (const sub of Object.keys(data.metadata)) {
              if (previous.metadata && sub in previous.metadata) {
                metadata[sub] = previous.metadata[sub];
              } else delete metadata[sub];
            }
            restored.metadata = metadata;
          }
          return restored as unknown as Artifact;
        });
      }
    },
    onSettled: (data, _err, variables) => {
      // `variables`, not `data`: `data` is undefined on error, and a failed
      // write must refetch too.
      if (!othersPending(variables.id)) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.artifacts.detail(variables.id),
        });
      }
      // Resolve effective parent ID
      const effectiveProjectId =
        data?.projectId ||
        (data?.parentEntityType === 'project' ? data?.parentEntityId : undefined);

      if (effectiveProjectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.artifacts.all('project', effectiveProjectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.children(effectiveProjectId),
        });
      }
      // Force hierarchy and recents to refetch immediately
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['recents'] });
    },
  });
}

/**
 * Delete an artifact with optimistic UI update
 */
export function useDeleteArtifact() {
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: async ({
      artifactId,
      projectId,
      artifactType,
    }: {
      artifactId: string;
      projectId?: string;
      artifactType?: string;
    }) => {
      const response = await deleteArtifact(apiService, artifactId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to delete artifact');
      }
      return { artifactId, projectId, artifactType };
    },
    onMutate: async ({ artifactId, projectId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.artifacts.detail(artifactId) });

      const previousArtifact = queryClient.getQueryData<Artifact>(
        queryKeys.artifacts.detail(artifactId),
      );

      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.artifacts.detail(artifactId) });

      // Remove from parent lists
      if (projectId) {
        queryClient.setQueryData<Artifact[]>(queryKeys.artifacts.all('project', projectId), (old) =>
          old?.filter((a) => a.id !== artifactId),
        );
      }

      return { previousArtifact };
    },
    onSuccess: (data) => {
      safeAnalytics.track(ANALYTICS_EVENTS.ITEM_TRASHED, {
        entity_type: data?.artifactType || 'artifact',
      } as any);

      const handleUndo = async () => {
        try {
          await restoreTrashItem(apiService, data.artifactId, 'artifact');
          safeAnalytics.track(ANALYTICS_EVENTS.TRASH_UNDO_USED, {
            entity_type: data?.artifactType || 'artifact',
          } as any);
          void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
          // The restored row's own detail. `onMutate` removeQueries'd it on
          // delete, but an undo can land while another surface has re-fetched
          // it — and ['artifacts'] no longer reaches details by prefix.
          void queryClient.invalidateQueries({
            queryKey: queryKeys.artifacts.detail(data.artifactId),
          });
          void queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
          void queryClient.invalidateQueries({ queryKey: ['recents'] });
          if (data.projectId) {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.projects.children(data.projectId),
            });
          }
          toast({ title: 'Item restored', status: 'success' });
        } catch {
          toast({ title: 'Failed to undo', status: 'error' });
        }
      };

      toast({
        duration: 8000,
        isClosable: true,
        render: ({ onClose }: { onClose: () => void }) =>
          createElement(ActionToast, {
            title: 'Moved to trash',
            description: 'This item will be permanently deleted after 30 days.',
            status: 'info',
            actionLabel: 'Undo',
            onAction: handleUndo,
            onClose,
          }),
      });
    },
    onError: (_err, { artifactId }, context) => {
      if (context?.previousArtifact) {
        queryClient.setQueryData(queryKeys.artifacts.detail(artifactId), context.previousArtifact);
      }
    },
    onSettled: (_data, _err, { projectId }) => {
      // Invalidate project children
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.children(projectId),
        });
      }
      // Force hierarchy and recents to refetch immediately
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['recents'] });
    },
  });
}
